import { useMemo, type ComponentProps, type ReactNode } from "react"
import type { Row } from "@tanstack/react-table"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Tooltip from "@/v2-ui/tooltip"
import { DotText } from "@/v2-backoffice-client/components/dot"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import Table, {
  type ColumnMetaMap,
  type TableProps,
  type PageInfo,
  createColumns,
  GROUPING_RANGE_DATES,
} from "@/v2-backoffice-client/components/table"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import SharingRiskBadge from "@/v2-backoffice-client/signIn/sharingRisk/SharingRiskBadge"
import type { SignInActivityFragment } from "@/v2-backoffice-client/signIn/__types__/signIn.fragment"

// Sortable risk magnitude for a row. Historic rows carry only the user's latest
// stored LEVEL, with Score 0 (see storedRiskDetail server-side), so sorting on
// Score alone tied every row on this table and the column looked unsortable.
// Falling back to the level keeps the ordering meaningful there while the live
// view still ranks within a level by its finer score.
function getRowRiskRank(row: Row<SignInActivityFragment>): number {
  const rank = (risk: SignInActivityFragment["SharingRisk"]) =>
    risk?.Score ? risk.Score : (risk?.Level ?? 1)

  if (row.original) return rank(row.original.SharingRisk)
  let max = 0
  for (const leaf of row.getLeafRows()) {
    const score = rank(leaf.original?.SharingRisk)
    if (score > max) max = score
  }
  return max
}

// Human label for a normalized sign-in source code. Unknown / null (pre-feature
// rows and unattributable events) read as a single "Unknown".
function formatSourceLabel(source: string | null | undefined): string {
  switch (source) {
    case "v2-console":
      return "V2 Console"
    case "v1-console":
      return "V1 Console"
    default:
      return "Unknown"
  }
}

// Raw failure-reason codes from the identity provider are cryptic. Map the known
// ones to a plain explanation a Backoffice user can act on; matched by substring
// so provider-prefixed variants ("SMS notAllowedByPolicy") still resolve.
// Unknown codes render as-is.
const FAILURE_REASON_HELP: Record<string, string> = {
  notAllowedByPolicy:
    "SMS sign-in is turned off by tenant policy. This is the normal default and doesn't block the user: the number still receives MFA codes.",
}

function describeFailureReason(reason: string): string | null {
  const lower = reason.toLowerCase()
  for (const [code, help] of Object.entries(FAILURE_REASON_HELP)) {
    if (lower.includes(code.toLowerCase())) return help
  }
  return null
}

function createSignInColumns(
  showUser: boolean,
  showSharingRisk: boolean,
  manualFiltering: boolean,
) {
  const columns: ColumnMetaMap<SignInActivityFragment> = {}

  if (showUser) {
    columns.UserName = {
      id: "UserName",
      label: "User",
      enableGrouping: true,
      getValue: (row) => formatUserName(row.FirstName, row.LastName),
      to: ({ row }) => {
        // row.original is undefined on a group header row (when grouping by
        // user), so guard before reading UserId — the header then renders the
        // user name as plain text instead of a link.
        if (!row.original?.UserId) return
        return `>>?v=user:${row.original.UserId}`
      },
    }
  }

  columns.Timestamp = {
    id: "Timestamp",
    label: "Time",
    defaultSize: 90,
    groupingRanges: GROUPING_RANGE_DATES,
    // Client-side tables (per-user/customer/tenant tabs) filter by date here.
    // The main /sign-ins page runs server-side (manualFiltering) and governs
    // the time window with its period selector, so a column date-filter there
    // would be a dead control (nothing maps it to the query) — omit it.
    ...(manualFiltering ? {} : { filter: { type: "date-filter" as const } }),
    renderCell({ row }) {
      return <RelativeDate date={row.original.Timestamp} />
    },
    exportValue({ row }) {
      return formatDateString(row.original.Timestamp)
    },
  }

  if (showUser && showSharingRisk) {
    columns.SharingRisk = {
      id: "SharingRisk",
      label: "Sharing Risk (latest)",
      description:
        "The signing-in user's most recently computed sharing-risk level, not a property of this one sign-in and not scored over the selected period. Risk is an aggregate over a user's history, so a 90-day-old row still shows the level as it stands now. The Users tab scores each user over the selected period; open a user for the signal breakdown.",
      defaultSize: 90,
      enableGrouping: true,
      // Rendered inline in the toolbar (Table.ToolbarFilters) for quick access
      // rather than tucked inside the filter panel.
      filter: {
        type: "select",
        placeholder: "Any risk",
        toolbar: true,
        options: [
          { label: "None", value: "1", dotVariant: "secondary" },
          { label: "Low", value: "2", dotVariant: "info" },
          { label: "Moderate", value: "3", dotVariant: "warning" },
          { label: "High", value: "4", dotVariant: "orange" },
          { label: "Critical", value: "5", dotVariant: "destructive" },
        ],
      },
      getValue(row) {
        return row.SharingRisk?.Level ?? 1
      },
      // Sort by the fine-grained score where there is one, so ordering ranks
      // users *within* a level (every High row would otherwise tie), falling
      // back to the level on historic rows that carry no score. getValue stays
      // on Level for the select filter and level-based grouping.
      sortingFn(rowA, rowB) {
        return getRowRiskRank(rowA) - getRowRiskRank(rowB)
      },
      renderCell({ row }) {
        return <SharingRiskBadge risk={row.original.SharingRisk} />
      },
      exportValue({ row }) {
        return row.original.SharingRisk?.LevelName ?? "None"
      },
    }
  }

  columns.IsFailure = {
    id: "IsFailure",
    label: "Status",
    description:
      "Whether the sign-in succeeded or failed. Failures include a wrong password, an expired or cancelled login, or a blocked account.",
    defaultSize: 90,
    enableGrouping: true,
    filter: {
      type: "select",
      placeholder: "Any status",
      options: [
        { label: "Success", value: "false" },
        { label: "Failed", value: "true" },
      ],
    },
    renderCell({ row }) {
      const failed = row.original.IsFailure
      return (
        <DotText variant={failed ? "destructive" : "success-1"}>
          {failed ? "Failed" : "Success"}
        </DotText>
      )
    },
    exportValue({ row }) {
      return row.original.IsFailure ? "Failed" : "Success"
    },
  }

  // Whether the account was blocked AT THE TIME of the attempt (not now). Lets
  // support isolate logins made while an account was blocked — the Status=Failed
  // filter alone conflates these with every other failure (wrong password,
  // expired token, …).
  columns.WasBlocked = {
    id: "WasBlocked",
    label: "Blocked",
    description:
      "The account was blocked when this attempt was made, so the sign-in was refused for that reason. Reflects the account's state at the time, not now.",
    defaultSize: 90,
    enableGrouping: true,
    filter: {
      type: "select",
      placeholder: "Any account",
      options: [
        { label: "Blocked at attempt", value: "true" },
        { label: "Not blocked", value: "false" },
      ],
    },
    renderCell({ row }) {
      return row.original.WasBlocked ? (
        <DotText variant="destructive">Blocked</DotText>
      ) : null
    },
    exportValue({ row }) {
      return row.original.WasBlocked ? "Blocked" : ""
    },
  }

  // Which app the sign-in came from. Derived from the B2C audit app id, so it
  // distinguishes the two consoles without a separate B2C app. Filter values
  // are the normalized source codes the backend stores/filters on.
  columns.Source = {
    id: "Source",
    label: "App",
    description:
      "Which app the sign-in came from: V2 Console, V1 Console, or Unknown when it can't be attributed.",
    defaultSize: 110,
    enableGrouping: true,
    filter: {
      type: "multi-select",
      placeholder: "Any app",
      options: [
        { label: "V2 Console", value: "v2-console" },
        { label: "V1 Console", value: "v1-console" },
        { label: "Unknown", value: "unknown" },
      ],
    },
    getValue(row) {
      return row.Source ?? "unknown"
    },
    renderCell({ row }) {
      return formatSourceLabel(row.original.Source)
    },
    exportValue({ row }) {
      return formatSourceLabel(row.original.Source)
    },
  }

  columns.Location = {
    id: "Location",
    label: "Location",
    description:
      "City and country the sign-in came from, estimated from the IP address.",
    enableGrouping: true,
    filter: { type: "text", placeholder: "Filter by location…" },
    renderCell({ row }) {
      return row.original.Location ?? null
    },
  }

  columns.IpAddress = {
    id: "IpAddress",
    label: "IP Address",
    defaultHidden: true,
    renderCell({ row }) {
      return row.original.IpAddress ?? null
    },
  }

  columns.UserAgent = {
    id: "UserAgent",
    label: "Device",
    description:
      "Browser and operating system, read from the sign-in's user agent.",
    defaultHidden: true,
    renderCell({ row }) {
      return row.original.UserAgent ?? null
    },
    exportValue({ row }) {
      return row.original.UserAgent ?? ""
    },
  }

  columns.Failure = {
    id: "Failure",
    label: "Failure Reason",
    description:
      "Why a failed sign-in was refused, as reported by the identity provider. For example, 'notAllowedByPolicy' on an SMS attempt means SMS sign-in is off by tenant policy; the number still receives MFA codes.",
    defaultHidden: true,
    renderCell({ row }) {
      const reason = row.original.Failure
      if (!reason) return null
      // Known provider codes get a plain-language tooltip; the raw code stays
      // visible (admins still want the exact value). Unknown codes render bare.
      const help = describeFailureReason(reason)
      if (!help) return reason
      return (
        <Tooltip>
          <Tooltip.Trigger asChild tabIndex={-1}>
            <span className="cursor-help">{reason}</span>
          </Tooltip.Trigger>
          <Tooltip.Content
            side="top"
            className="ui-tooltip-content--panel max-w-[280px]"
          >
            {help}
          </Tooltip.Content>
        </Tooltip>
      )
    },
  }

  return createColumns<SignInActivityFragment>({
    enableSorting: true,
    enableHiding: true,
    columns: columns,
  })
}

export type SignInTableProps = Omit<
  TableProps<SignInActivityFragment>,
  "data" | "columns"
> &
  ComponentProps<"div"> & {
    activities: SignInActivityFragment[]
    isLoading?: boolean
    error?: Error
    showUser?: boolean
    /** Show the per-row Sharing Risk column (only meaningful with showUser). */
    showSharingRisk?: boolean
    tableId: string
    exportFileName: string
    onRefresh?(): void
    /** Server pagination info; when set, the count shows "x of total" and the
     *  body loads more on scroll. Omit for fully client-loaded data. */
    pageInfo?: PageInfo | null
    /** Loads the next page (infinite scroll). Paired with pageInfo. */
    onFetchMore?(): void
    /** Extra controls rendered in the toolbar, e.g. a period selector. */
    toolbarExtra?: ReactNode
  }

function SignInTable(props: SignInTableProps) {
  const {
    className,
    activities,
    isLoading,
    error,
    showUser = false,
    showSharingRisk = true,
    tableId,
    exportFileName,
    onRefresh,
    pageInfo,
    onFetchMore,
    toolbarExtra,
    ...tableProps
  } = props

  const manualFiltering = tableProps.manualFiltering ?? false
  const columns = useMemo(
    () => createSignInColumns(showUser, showSharingRisk, manualFiltering),
    [showUser, showSharingRisk, manualFiltering],
  )

  return (
    <Table
      className={cn("sign-in-table", className)}
      tableId={tableId}
      data={activities}
      columns={columns}
      enableGrouping
      enableRowSelection={false}
      {...tableProps}
    >
      <Table.Toolbar>
        <div className="flex-1" />
        <Table.Search placeholder="Search sign-ins…" />
        {toolbarExtra}
        <Table.ToolbarFilters />

        <Separator orientation="vertical" />
        <Table.ToolbarMenu
          onRefresh={onRefresh}
          exportFileName={exportFileName}
        />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />
      <Table.Content
        countLabel="sign-ins"
        isLoading={isLoading}
        error={error}
        pageInfo={pageInfo}
        onFetchMore={onFetchMore}
      />
    </Table>
  )
}

export default SignInTable
